import { useRoute, Link } from "wouter";
import { useMemo } from "react";
import { SmartLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { useGetTrainerBySlug, useGetTrainerAvailability, useListLocations, useListLocationAvailability, type AvailabilityBlock } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Star, Calendar, ArrowRight, User, MapPin, Award,
  Clock, Play, Shield
} from "lucide-react";

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function clipBlockToGymHours(
  block: AvailabilityBlock,
  gymDayBlocks: { startTime: string; endTime: string }[]
): AvailabilityBlock[] {
  if (gymDayBlocks.length === 0) return [];
  const bStart = timeToMinutes(block.startTime);
  const bEnd = timeToMinutes(block.endTime);
  const clipped: AvailabilityBlock[] = [];
  for (const gh of gymDayBlocks) {
    const gStart = timeToMinutes(gh.startTime);
    const gEnd = timeToMinutes(gh.endTime);
    const overlapStart = Math.max(bStart, gStart);
    const overlapEnd = Math.min(bEnd, gEnd);
    if (overlapStart < overlapEnd) {
      clipped.push({
        ...block,
        startTime: minutesToTime(overlapStart),
        endTime: minutesToTime(overlapEnd),
      });
    }
  }
  return clipped;
}

export default function TrainerPublicProfile() {
  const [, params] = useRoute("/trainer/:slug");
  const slug = params?.slug || "";
  const { data: trainer, isLoading, error } = useGetTrainerBySlug(slug, {
    query: { enabled: !!slug, retry: false },
  });

  const { data: availabilityBlocks } = useGetTrainerAvailability(trainer?.id || "", undefined, {
    query: { enabled: !!trainer?.id },
  });

  const { data: locations = [] } = useListLocations(trainer?.gymId ?? "", {
    query: { enabled: !!trainer?.gymId },
  });

  const effectiveLocation = useMemo(() => {
    if (!locations.length) return undefined;
    if (trainer?.primaryLocationId) {
      const loc = locations.find(l => l.id === trainer.primaryLocationId && l.isActive);
      if (loc) return loc;
    }
    return locations.find(l => l.isPrimary && l.isActive) ?? locations.find(l => l.isActive);
  }, [locations, trainer?.primaryLocationId]);

  const { data: locationBlocks = [] } = useListLocationAvailability(effectiveLocation?.id ?? "", {
    query: { enabled: !!effectiveLocation },
  });

  const gymHoursByDay = useMemo(() => {
    const map: Record<number, { startTime: string; endTime: string }[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    {
      locationBlocks.filter(b => b.isActive).forEach(b => {
        map[b.dayOfWeek].push({ startTime: b.startTime, endTime: b.endTime });
      });
    }
    return map;
  }, [locationBlocks]);

  const availByDay = useMemo(() => {
    const map: Record<number, AvailabilityBlock[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    if (availabilityBlocks && Array.isArray(availabilityBlocks)) {
      (availabilityBlocks as AvailabilityBlock[])
        .filter(b => b.isActive)
        .forEach(b => {
          const clipped = clipBlockToGymHours(b, gymHoursByDay[b.dayOfWeek] || []);
          clipped.forEach(cb => map[cb.dayOfWeek].push(cb));
        });
    }
    return map;
  }, [availabilityBlocks, gymHoursByDay]);

  const hasAvailability = Object.values(availByDay).some(arr => arr.length > 0);

  if (isLoading) {
    return (
      <SmartLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SmartLayout>
    );
  }

  if (error || !trainer) {
    return (
      <SmartLayout>
        <SEOHead
          title="Trainer Not Found"
          description="This trainer doesn't exist or may have been removed."
          canonicalPath={`/trainer/${slug}`}
          noindex
        />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <User className="w-12 h-12 text-white/20" />
          <h1 className="text-2xl font-display font-bold text-white">Trainer not found</h1>
          <p className="text-white/50">This trainer doesn't exist or may have been removed.</p>
          <Link href="/explore/trainers">
            <Button variant="outline" className="border-white/10 mt-4">Browse All Trainers</Button>
          </Link>
        </div>
      </SmartLayout>
    );
  }

  const fullName = `${trainer.firstName} ${trainer.lastName}`;
  const videoEmbedUrl = getVideoEmbedUrl(trainer.introVideoUrl);
  const specialtiesList = trainer.specialties?.length ? trainer.specialties.join(", ") : "";

  return (
    <SmartLayout>
      <SEOHead
        title={`${fullName} — Martial Arts Trainer${trainer.gymName ? ` at ${trainer.gymName}` : ""}`}
        description={`Book a private lesson with ${fullName}${trainer.gymName ? ` at ${trainer.gymName}` : ""}. ${specialtiesList ? `Specialties: ${specialtiesList}. ` : ""}${trainer.hourlyRate ? `$${trainer.hourlyRate}/hr. ` : ""}${trainer.totalSessions} sessions completed.`}
        canonicalPath={`/trainer/${slug}`}
      />

      <div className="py-12 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-28 h-28 rounded-2xl bg-secondary flex items-center justify-center text-4xl font-bold text-white shrink-0 overflow-hidden">
                {trainer.avatarUrl ? (
                  <img src={trainer.avatarUrl} alt={`${fullName} profile photo`} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  trainer.firstName[0]
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-2">
                  {fullName}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {trainer.avgRating != null && (
                    <span className="flex items-center gap-1.5 text-primary">
                      <Star className="w-5 h-5 fill-primary" />
                      <span className="text-lg font-bold">{Number(trainer.avgRating).toFixed(1)}</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-white/50">
                    <Calendar className="w-4 h-4" />
                    {trainer.totalSessions} sessions
                  </span>
                  {trainer.yearsExperience != null && (
                    <span className="flex items-center gap-1.5 text-white/50">
                      <Clock className="w-4 h-4" />
                      {trainer.yearsExperience} {trainer.yearsExperience === 1 ? "year" : "years"} experience
                    </span>
                  )}
                </div>

                {trainer.gymName && (
                  <Link href={`/gym/${trainer.gymSlug}`}>
                    <span className="text-sm text-white/40 hover:text-primary transition-colors flex items-center gap-1.5 cursor-pointer mb-4 inline-flex">
                      <MapPin className="w-4 h-4" />
                      {trainer.gymName}
                    </span>
                  </Link>
                )}

                {trainer.hourlyRate != null && (
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-mono text-primary font-bold">
                      ${trainer.hourlyRate}/hr
                    </span>
                    <Link href={`/book?trainerId=${trainer.id}&gymId=${trainer.gymId || ""}`}>
                      <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-12 px-8 rounded-xl shadow-lg shadow-primary/20 gap-2">
                        Book a Session
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                )}
                {trainer.hourlyRate == null && (
                  <Link href={`/book?trainerId=${trainer.id}&gymId=${trainer.gymId || ""}`}>
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-12 px-8 rounded-xl shadow-lg shadow-primary/20 gap-2">
                      Book a Session
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>

          {trainer.bio && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10"
            >
              <h2 className="text-xl font-display font-bold text-white mb-3">About</h2>
              <p className="text-white/60 leading-relaxed whitespace-pre-line">{trainer.bio}</p>
            </motion.section>
          )}

          {videoEmbedUrl && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-10"
            >
              <h2 className="text-xl font-display font-bold text-white mb-3 flex items-center gap-2">
                <Play className="w-5 h-5 text-primary" />
                Intro Video
              </h2>
              <div className="rounded-2xl overflow-hidden border border-white/[0.06] aspect-video">
                <iframe
                  src={videoEmbedUrl}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${fullName} intro video`}
                  loading="lazy"
                />
              </div>
            </motion.section>
          )}

          {trainer.specialties && trainer.specialties.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-10"
            >
              <h2 className="text-xl font-display font-bold text-white mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Specialties
              </h2>
              <div className="flex flex-wrap gap-2">
                {trainer.specialties.map((s) => (
                  <span
                    key={s}
                    className="px-4 py-1.5 bg-white/[0.04] rounded-xl text-sm text-white/60 border border-white/[0.08]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </motion.section>
          )}

          {trainer.certifications && trainer.certifications.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-10"
            >
              <h2 className="text-xl font-display font-bold text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Certifications
              </h2>
              <div className="flex flex-wrap gap-2">
                {trainer.certifications.map((c) => (
                  <span
                    key={c}
                    className="px-4 py-1.5 bg-primary/10 rounded-xl text-sm text-primary border border-primary/20"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </motion.section>
          )}

          {hasAvailability && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-10"
            >
              <h2 className="text-xl font-display font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Weekly Availability
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {DAYS_SHORT.map((day, i) => {
                  const dayBlocks = availByDay[i];
                  const hasSlots = dayBlocks.length > 0;
                  return (
                    <div
                      key={i}
                      className={`text-center p-3 rounded-xl ${hasSlots ? "bg-primary/5 border border-primary/10" : "bg-white/[0.02]"}`}
                    >
                      <div className={`text-xs font-semibold mb-1 ${hasSlots ? "text-white" : "text-muted-foreground/50"}`}>
                        {day}
                      </div>
                      {hasSlots ? (
                        <div className="space-y-1">
                          {dayBlocks.map((b, idx) => (
                            <div key={idx} className="text-xs text-primary">
                              {formatTime(b.startTime)} – {formatTime(b.endTime)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground/30">Off</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-center">
                <Link href={`/book?trainerId=${trainer.id}&gymId=${trainer.gymId || ""}`}>
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-10 px-6 rounded-xl gap-2">
                    View Available Slots & Book
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.section>
          )}
        </div>
      </div>
    </SmartLayout>
  );
}

function getVideoEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}
